import { type FC, useEffect, useRef } from 'react'
import { Checkbox } from '@/components/commons/checkbox'
import { HelpCircleIcon } from '@/components/ui/icons'
import { Tooltip } from '@/components/ui/tooltip'
import { useAddImagesFlag } from '@/features/photoset/queries/use-add-images-flag'
import {
	type RemoveImagesFlagData,
	useRemoveImagesFlag
} from '@/features/photoset/queries/use-remove-images-flag'
import type { FlagWithLabel, Image, Photoset } from '@/features/photoset/types'
import { errorHandler } from '@/helpers/utils'
import { useTranslation } from '@/translations/i18n'

interface FlagsPopoverItemProps {
	selectedImagesIds: Image['id'][]
	flag: FlagWithLabel
	stepId: Photoset['step_id']
	defaultChecked?: boolean
	isIndeterminate?: boolean
	photosetSpaceIds?: number[]
	isUpdateDisabled?: boolean
	hideDescription?: boolean
	onRemoveFlagCb?: (data: RemoveImagesFlagData) => void
}

export const FlagsPopoverItem: FC<FlagsPopoverItemProps> = ({
	selectedImagesIds,
	flag,
	stepId,
	defaultChecked = false,
	isIndeterminate = false,
	photosetSpaceIds,
	isUpdateDisabled = false,
	hideDescription = false,
	onRemoveFlagCb
}) => {
	const { t } = useTranslation()
	const checkboxRef = useRef<HTMLInputElement | null>(null)
	const addImagesFlag = useAddImagesFlag({
		photosetSpaceIds,
		stepId,
		options: {
			onError: (error) => errorHandler(error)
		}
	})

	const removeImagesFlag = useRemoveImagesFlag({
		photosetSpaceIds,
		stepId,
		options: {
			onError: (error) => errorHandler(error),
			onSuccess: onRemoveFlagCb
		}
	})

	useEffect(() => {
		if (checkboxRef?.current && isIndeterminate) {
			checkboxRef.current.indeterminate = true
		}
	}, [isIndeterminate])

	const handleUpdateFlagToImages = () => {
		const mutationFunction = defaultChecked
			? removeImagesFlag
			: addImagesFlag
		mutationFunction.mutate({ flag, imageIds: selectedImagesIds })
	}

	return (
		<div className='flex w-full items-center justify-between gap-2 overflow-hidden truncate px-3 py-2 font-medium text-neutral-700 text-sm transition-all duration-150'>
			<div className='flex items-center gap-2'>
				<Checkbox
					defaultChecked={defaultChecked}
					disabled={
						(addImagesFlag.isPending &&
							!removeImagesFlag.isPending) ||
						isUpdateDisabled
					}
					onChange={handleUpdateFlagToImages}
					ref={checkboxRef}
				/>
				<span>{flag.label}</span>
			</div>

			{!hideDescription && (
				<Tooltip
					content={
						<p>
							{t(`photoset:flags.${flag.key_name}.description`)}
						</p>
					}
					contentProps={{ sideOffset: 8, align: 'end' }}
					variant='light'
				>
					<HelpCircleIcon className='fill-neutral-400' size={16} />
				</Tooltip>
			)}
		</div>
	)
}
